import React, { Fragment } from 'react';

import { HASHTAG_TYPE } from './../../ricos-content';
import type { Node } from './../../ricos-schema/legacy/wix/rich_content/v1';
import { Decoration_Type, Link_Target } from './../../ricos-schema/legacy/wix/rich_content/v1';
import type { NodeViewDecorator, ViewerContextualData } from './../../ricos-types';

import { hashtagRegex } from '../const';
import styles from '../statics/hashtag-scss';

const hasLink = (node: Node) => {
  return node.textData?.decorations?.some(decoration =>
    [Decoration_Type.LINK, Decoration_Type.ANCHOR, Decoration_Type.MENTION].includes(
      decoration.type
    )
  );
};

const decorator = ({
  text,
  contextualData,
  node,
}: {
  text: string;
  contextualData: ViewerContextualData;
  node: Node;
}) => {
  const settings = contextualData.config[HASHTAG_TYPE] || {};
  const { createHref, onClick } = settings;
  const { anchorTarget = Link_Target.SELF } = contextualData;
  const target = `_${anchorTarget?.toLowerCase?.()}`;
  const href = createHref ? createHref(text) : null;

  const decoratedOnClick = (e: $TSFixMe) => onClick(e, text);
  const onKeyDown = (e: $TSFixMe) => (e.key === 'Enter' || e.key === ' ') && onClick(e, text);

  const key = `hashtag-${text}`;

  const colorDecoration = node.textData?.decorations?.find(
    ({ type }) => type === Decoration_Type.COLOR
  );
  const foregroundColor = colorDecoration?.colorData?.foreground;

  return href ? (
    <a
      key={key}
      className={styles.hashtag}
      href={href}
      target={target}
      onClick={decoratedOnClick}
      onKeyDown={onKeyDown}
    >
      <span
        style={foregroundColor ? { color: foregroundColor, textDecoration: 'inherit' } : undefined}
      >
        {text}
      </span>
    </a>
  ) : (
    <span key={key} className={styles.hashtag}>
      {text}
    </span>
  );
};

export const nodeViewDecorator: NodeViewDecorator<JSX.Element> = {
  rule: (node: Node) => !!(node.textData?.text.match(hashtagRegex) && !hasLink(node)),
  decorate: ({ node, context }) => {
    const hashtags: [from: number, to: number][] = [];
    const spans: JSX.Element[] = [];
    const { text = '' } = node.textData || {};
    Array.from(text.matchAll(hashtagRegex)).forEach(match => {
      const hash = match[0];
      let hashLength = hash.length;
      let from = match.index || 0;
      if (!hash.startsWith('#')) {
        from++;
        hashLength--;
      }
      const to = from + hashLength;
      hashtags.push([from, to]);
    });
    let start = 0;
    const end = text.length;
    hashtags.forEach(([from, to]) => {
      if (from > start) {
        spans.push(<span key={`${text}-${start}`}>{text.substring(start, from)}</span>);
      }
      spans.push(
        <Fragment key={`${text}-${from}`}>
          {decorator({ text: text.substring(from, to), contextualData: context, node })}
        </Fragment>
      );
      start = to;
    });
    const lastHashTag = hashtags[hashtags.length - 1][1];
    if (lastHashTag < end) {
      spans.push(<span key={`${text}-${end}`}>{text.substring(lastHashTag, end)}</span>);
    }
    return <Fragment key={'hastagContainer'}>{spans}</Fragment>;
  },
  isText: true,
};
